import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Login() {
  const navigate = useNavigate();
  const { user, isLoading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/');
    }
  }, [user, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login(email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      // Clean up AWS Amplify error messages
      if (message.includes('Incorrect username or password')) {
        setError('Incorrect email or password');
      } else if (message.includes('User does not exist')) {
        setError('No account found with this email');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Gradient Background with Randomized Boxes Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent-purple via-primary-500 to-accent-orange">
        {/* Multiple overlapping box layers for randomized effect */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80px 80px at 20% 30%, rgba(255,255,255,0.08) 0%, transparent 50%),
              radial-gradient(ellipse 60px 60px at 70% 20%, rgba(0,0,0,0.06) 0%, transparent 50%),
              radial-gradient(ellipse 90px 90px at 40% 70%, rgba(255,255,255,0.07) 0%, transparent 50%),
              radial-gradient(ellipse 70px 70px at 80% 60%, rgba(0,0,0,0.05) 0%, transparent 50%),
              radial-gradient(ellipse 85px 85px at 10% 80%, rgba(255,255,255,0.06) 0%, transparent 50%),
              radial-gradient(ellipse 75px 75px at 60% 40%, rgba(0,0,0,0.04) 0%, transparent 50%),
              radial-gradient(ellipse 65px 65px at 90% 90%, rgba(255,255,255,0.05) 0%, transparent 50%),
              radial-gradient(ellipse 55px 55px at 30% 10%, rgba(0,0,0,0.06) 0%, transparent 50%)
            `,
          }}
        />
        {/* Secondary scattered boxes */}
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: `
              radial-gradient(square, rgba(255,255,255,0.1) 0%, transparent 70%)
            `,
            backgroundSize: '120px 120px',
            backgroundPosition: '30px 45px',
          }}
        />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              radial-gradient(circle 40px at 25% 25%, rgba(255,255,255,0.08) 0%, transparent 100%),
              radial-gradient(circle 50px at 75% 35%, rgba(0,0,0,0.06) 0%, transparent 100%),
              radial-gradient(circle 45px at 45% 85%, rgba(255,255,255,0.07) 0%, transparent 100%),
              radial-gradient(circle 55px at 85% 75%, rgba(255,255,255,0.05) 0%, transparent 100%),
              radial-gradient(circle 35px at 15% 55%, rgba(0,0,0,0.05) 0%, transparent 100%)
            `,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col md:flex-row w-full">
        {/* Left side - Branding */}
        <div className="flex-1 flex flex-col justify-center px-8 py-12 md:px-16 lg:px-24">
          <div className="max-w-lg">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-black text-white leading-tight mb-6">
              SCORE<br />LEDGER
            </h1>
            <p className="text-white/90 text-lg md:text-xl">
              Track your team&apos;s performance across sprints and product increments.
            </p>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div className="flex-1 flex items-center justify-center px-4 py-12 md:px-8">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 text-gray-900">
              {/* Form Header */}
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-accent-purple via-primary-500 to-accent-orange rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-white font-bold text-xl">SL</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
                <p className="text-gray-500 mt-1">Sign in to your account</p>
              </div>

              {/* Login form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-accent-purple via-primary-500 to-accent-orange text-white font-semibold py-3.5 px-4 rounded-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                Don&apos;t have an account?{' '}
                <span className="text-primary-500 font-medium">Contact your administrator</span>
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
